/*
 * initialize-socket.js  (ES5 + jQuery 3, load FIRST)
 * One authenticated websocket: token via $.ajax -> terminal.hello -> terminal.welcome.
 * Heartbeat, reconnect with backoff, reconnects at once when the app returns to the foreground.
 *
 * The same socket carries three channels, by message type prefix:
 *   terminal.*  payments / print with terminals   (terminal-pos.js, terminal-device.js)
 *   screen.*    POS -> its customer screens        (terminal-screen.js)
 *   main.*      POS -> the main POS                (terminal-main.js)
 *
 * Load order: terminal-socket.js, terminal-pos.js, terminal-screen.js, terminal-main.js,
 *             terminal-device.js, terminal-ui.js
 */
(function (window, $) {
  'use strict';
  var T = (window.SocketHub = window.SocketHub || {});

  // message types the socket passes on; anything else is ignored
  var PREFIX = /^(terminal|screen|main|relay)\./;

  // ---- shared defaults: every init function falls back to these ---------------------
  T.config = {
    tokenUrl: '/api/v1/private/web-socket/token',
    url: function () {
      var h = location.hostname;
      return location.protocol === 'https:'
        ? 'wss://' + h + '/ws'
        : 'ws://' + h + ':9090/';
    },
    deviceId: function () {
      return (
        localStorage.getItem('deviceID') || $('body').attr('device_id') || ''
      );
    },
  };

  // SocketHub.ensurePos(opts) can be called from ANY file, even one that runs before
  // TerminalUi.js has loaded: the call is queued and answered as soon as the real
  // implementation is installed by TerminalUi.js.
  var ensureQueue = [];
  T.ensurePos = function (opts) {
    var d = $.Deferred();
    ensureQueue.push({ opts: opts, d: d });
    return d.promise();
  };
  T._installEnsure = function (impl) {
    T.ensurePos = impl;
    while (ensureQueue.length) {
      var q = ensureQueue.shift();
      impl(q.opts).then(q.d.resolve, q.d.reject);
    }
  };
  var hubSeq = 0;

  T.MSG = {
    HELLO: 'terminal.hello',
    WELCOME: 'terminal.welcome',
    ERROR: 'terminal.error',
    PING: 'terminal.ping',
    PONG: 'terminal.pong',
    REPLACED: 'terminal.replaced',
    PEER: 'terminal.peer',
    STATE: 'terminal.state',
    BUSY: 'terminal.busy',
    TX_UNKNOWN: 'terminal.tx_unknown',
    RESULT_ACK: 'terminal.result_ack',
    PAYMENT_REQUEST: 'terminal.payment_request',
    CANCEL: 'terminal.cancel',
    STATUS_REQUEST: 'terminal.status_request',
    PRINT: 'terminal.print',
    RELEASE: 'terminal.release',
    PAYMENT_RESULT: 'terminal.payment_result',
    PROGRESS: 'terminal.progress',
    STATUS: 'terminal.status',
    AVAILABILITY: 'terminal.availability',

    // customer screens
    SCREEN_STATE: 'screen.state', // POS -> screens (cached on the server), server -> screen
    SCREEN_EVENT: 'screen.event', // POS -> screens, one-off
    SCREEN_INPUT: 'screen.input', // screen -> its POS
    SCREEN_REQUEST_STATE: 'screen.request_state', // screen -> server
    SCREEN_PRESENCE: 'screen.presence', // server -> POS {count, screens}
    SCREEN_POS: 'screen.pos', // server -> screen {online}
    SCREEN_ERROR: 'screen.error',

    // main POS relay
    MAIN_REQUEST: 'main.request', // POS -> server -> main
    MAIN_PROGRESS: 'main.progress', // main -> server -> asking POS
    MAIN_REPLY: 'main.reply', // main -> server -> asking POS
    MAIN_ERROR: 'main.error', // server -> asking POS
    MAIN_LOST: 'main.lost', // server -> asking POS (main left with the request open)
    MAIN_PRESENCE: 'main.presence', // server -> POS {online, id}
    MAIN_ROLE: 'main.role', // server -> POS {main}
    MAIN_ASSIGN: 'main.assign', // POS -> server {token} the backend just changed the main POS
    MAIN_ASSIGNED: 'main.assigned', // server -> that POS {id, online}

    // general POS <-> POS relay
    RELAY_SEND: 'relay.send', // POS -> server {msgId, to?, topic, data, expectReply?}
    RELAY_MESSAGE: 'relay.message', // server -> POS {msgId, from, topic, data, broadcast, expectReply}
    RELAY_SENT: 'relay.sent', // server -> sender {msgId, count}
    RELAY_REPLY: 'relay.reply', // POS -> server -> asker {msgId, to/from, status, reason, data}
    RELAY_ERROR: 'relay.error', // server -> sender {msgId, code}
    RELAY_PRESENCE: 'relay.presence', // server -> POS {id, online}
  };

  // success | declined | cancelled | failed | unknown (unknown = verify against the backend)
  T.RESULT = {
    SUCCESS: 'success',
    DECLINED: 'declined',
    CANCELLED: 'cancelled',
    FAILED: 'failed',
    UNKNOWN: 'unknown',
  };

  T.newId = function () {
    var c = window.crypto || window.msCrypto,
      a = new Uint8Array(16),
      s = '',
      i;
    if (c && c.getRandomValues) c.getRandomValues(a);
    else for (i = 0; i < 16; i++) a[i] = Math.floor(Math.random() * 256);
    for (i = 0; i < 16; i++) s += (a[i] < 16 ? '0' : '') + a[i].toString(16);
    return s;
  };

  /**
   * opts: url, tokenUrl, role ('pos'|'terminal'|'screen'), deviceId,
   *       onReady(welcome), onMessage(msg), onClose(), onReplaced(), onFatal()
   */
  T.createSocket = function (opts) {
    var ws = null,
      state = 'idle'; // idle | closed | connecting | authenticating | ready
    var gen = 0,
      attempt = 0,
      lastSeen = 0,
      want = false;
    var retryT = null,
      pingT = null,
      connT = null;
    var ns = '.hub' + ++hubSeq; // event namespace, so destroy() can unbind exactly our handlers

    function fetchToken(done, fail) {
      $.ajax({
        url: opts.tokenUrl,
        method: 'POST',
        data: {
          role: opts.role,
          deviceId: opts.deviceId,
          organizerId: opts.organizerId,
        },
      })
        .done(function (res) {
          var r = res && res.data;
          if (r && typeof r.token === 'string') done(r.token);
          else fail(0);
        })
        .fail(function (xhr) {
          fail(xhr.status);
        });
    }

    function connect() {
      if (
        !want ||
        state === 'connecting' ||
        state === 'authenticating' ||
        state === 'ready'
      )
        return;
      var myGen = ++gen;
      state = 'connecting';
      fetchToken(
        function (token) {
          if (myGen === gen) open(token, myGen);
        },
        function (status) {
          if (myGen !== gen) return;
          state = 'closed';
          if (status === 401 || status === 403) {
            // logged out: stop, do not hammer the endpoint
            want = false;
            if (opts.onFatal) opts.onFatal();
            return;
          }
          console.log('WebSocket token fetch failed', status);
          scheduleRetry();
        }
      );
    }

    function open(token, myGen) {
      var sock;
      try {
        sock = new WebSocket(opts.url);
      } catch (e) {
        console.log('WebSocket open failed', e);
        state = 'closed';
        scheduleRetry();
        return;
      }
      ws = sock;
      connT = setTimeout(function () {
        console.log('WebSocket connection timeout', state, myGen, gen);
        if (myGen === gen && state !== 'ready') drop();
      }, 10000);
      sock.onopen = function () {
        if (myGen !== gen) return;
        state = 'authenticating';
        lastSeen = Date.now();
        sock.send(JSON.stringify({ type: T.MSG.HELLO, token: token }));
      };
      sock.onmessage = function (ev) {
        if (myGen === gen) handle(ev.data);
      };
      sock.onclose = function () {
        if (myGen === gen) drop();
      };
    }

    function handle(raw) {
      var m;
      try {
        m = JSON.parse(raw);
      } catch (e) {
        return;
      }
      if (!m || typeof m.type !== 'string' || !PREFIX.test(m.type)) return;
      lastSeen = Date.now();
      if (m.type === T.MSG.PONG) return;

      if (state === 'authenticating') {
        if (m.type === T.MSG.WELCOME) {
          state = 'ready';
          attempt = 0;
          clearTimeout(connT);
          startPing();
          if (opts.onReady) opts.onReady(m);
        } else if (m.type === T.MSG.ERROR) {
          drop();
        }
        return;
      }
      if (state !== 'ready') return;

      if (m.type === T.MSG.REPLACED) {
        // another instance of this device took over: do not fight it
        want = false;
        teardown(1000);
        state = 'idle';
        if (opts.onReplaced) opts.onReplaced();
        return;
      }
      if (opts.onMessage) opts.onMessage(m);
    }

    function startPing() {
      clearInterval(pingT);
      pingT = setInterval(function () {
        if (Date.now() - lastSeen > 30000) {
          drop();
          return;
        }
        send(T.MSG.PING);
      }, 10000);
    }

    function drop() {
      if (state === 'closed' || state === 'idle') return;
      var wasReady = state === 'ready';
      teardown(4000);
      state = 'closed';
      if (wasReady && opts.onClose) opts.onClose();
      console.log('WebSocket dropped, will retry in a moment', want);
      if (want) scheduleRetry();
    }

    function teardown(code) {
      gen++; // invalidates every callback of the old socket
      clearInterval(pingT);
      clearTimeout(connT);
      var s = ws;
      ws = null;
      if (s) {
        s.onopen = s.onmessage = s.onclose = s.onerror = null;
        try {
          s.close(code || 1000);
        } catch (e) {}
      }
    }

    function scheduleRetry() {
      clearTimeout(retryT);
      var base = Math.min(1000 * Math.pow(1.5, attempt), 10000);
      attempt++;
      retryT = setTimeout(connect, base / 2 + Math.random() * (base / 2));
    }

    function send(type, payload) {
      if (state !== 'ready' || !ws || ws.readyState !== 1) return false;
      var o = $.extend({}, payload);
      o.type = type; // applied last: a payload can never override the type
      try {
        ws.send(JSON.stringify(o));
        return true;
      } catch (e) {
        return false;
      }
    }

    /** cb() runs when the send buffer is empty (or after timeoutMs). */
    function flush(cb, timeoutMs) {
      var t0 = Date.now(),
        limit = timeoutMs || 500;
      (function tick() {
        if (!ws || ws.bufferedAmount === 0 || Date.now() - t0 > limit) {
          cb();
          return;
        }
        setTimeout(tick, 25);
      })();
    }

    function wake() {
      if (!want) return;
      if (state === 'ready') {
        var sent = Date.now();
        send(T.MSG.PING);
        setTimeout(function () {
          if (state === 'ready' && lastSeen < sent) drop();
        }, 4000);
      } else if (state === 'closed') {
        clearTimeout(retryT);
        attempt = 0;
        connect();
      }
    }

    $(document).on('visibilitychange' + ns, function () {
      if (document.visibilityState === 'visible') wake();
    });
    $(window).on('pageshow' + ns + ' online' + ns, wake);
    $(window).on('offline' + ns, function () {
      if (want) drop();
    });

    return {
      start: function () {
        want = true;
        connect();
      },
      stop: function () {
        want = false;
        clearTimeout(retryT);
        teardown(1000);
        state = 'idle';
      },
      // stop for good: closes the socket AND removes the page-level handlers
      destroy: function () {
        want = false;
        clearTimeout(retryT);
        teardown(1000);
        state = 'idle';
        $(document).off(ns);
        $(window).off(ns);
      },
      send: send,
      flush: flush,
      wake: wake,
      isReady: function () {
        return state === 'ready' && !!ws && ws.readyState === 1;
      },
    };
  };
})(window, jQuery);
